import { useState } from "react";
import { ModuleAccessGuard } from "@/components/ModuleAccessGuard";
import { DataTable } from "@/components/shared/DataTable";
import { FilterBar } from "@/components/shared/FilterBar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { useAccounts, useCreateAccount, useUpdateAccount, useDeleteAccount } from "@/hooks/useOnboarding";
import { useToast } from "@/hooks/use-toast";
import { CreditCard, Users, Building, Plus, Edit, Trash2, MoreHorizontal } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import type { Column } from "@/components/shared/DataTable";

export default function Accounts() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState<Record<string, any>>({});
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState<any>(null);
  const [hasParent, setHasParent] = useState(false);
  const { toast } = useToast();

  const offset = (page - 1) * 20;
  const { data: accountsData, isLoading } = useAccounts({ offset, limit: 20, search, ...filters });
  const createAccount = useCreateAccount();
  const updateAccount = useUpdateAccount();
  const deleteAccount = useDeleteAccount();

  const [formData, setFormData] = useState({
    customer_id: "",
    supplier_id: "",
    account_number: "",
    commodity: "",
    frequency: "",
    bill_source: "",
    site_id: "",
    parent_account_number: "",
    address: "",
    city: "",
    state: "",
    country: "",
    postal_code: "",
    account_start_date: "",
    account_end_date: "",
    identifier_number: "",
    status: "active"
  });

  const handleSubmit = async () => {
    try {
      if (editingAccount) {
        await updateAccount.mutateAsync({ accountId: editingAccount.id, updates: formData });
        toast({ title: "Account updated successfully" });
      } else {
        await createAccount.mutateAsync(formData);
        toast({ title: "Account created successfully" });
      }
      setIsCreateOpen(false);
      setEditingAccount(null);
      resetForm();
    } catch (error) {
      toast({ title: "Error", description: String(error), variant: "destructive" });
    }
  };

  const resetForm = () => {
    setFormData({
      customer_id: "", supplier_id: "", account_number: "", commodity: "", frequency: "",
      bill_source: "", site_id: "", parent_account_number: "", address: "", city: "",
      state: "", country: "", postal_code: "", account_start_date: "", account_end_date: "",
      identifier_number: "", status: "active"
    });
    setHasParent(false);
  };

  const columns: Column<any>[] = [
    { key: "customer_name", header: "Customer", render: (item) => item.customer_name },
    { key: "supplier_name", header: "Supplier", render: (item) => item.supplier_name },
    { key: "account_number", header: "Account Number", render: (item) => item.account_number },
    { key: "address", header: "Address", render: (item) => item.address },
    { key: "commodity", header: "Commodity", render: (item) => item.commodity },
    { key: "site_name", header: "Site Name", render: (item) => item.site_name },
    { key: "bill_source", header: "Bill Source", render: (item) => item.bill_source },
    {
      key: "status",
      header: "Status",
      render: (item) => {
        const status = item.status;
        const variant = status === "active" ? "default" : status === "suspended" ? "destructive" : "secondary";
        return <Badge variant={variant}>{status}</Badge>;
      }
    },
    {
      key: "actions",
      header: "Actions",
      render: (item) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon"><MoreHorizontal className="h-4 w-4" /></Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => { setEditingAccount(item); setIsCreateOpen(true); }}>
              <Edit className="mr-2 h-4 w-4" />Edit
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="text-destructive" onClick={() => deleteAccount.mutate(item.id)}>
              <Trash2 className="mr-2 h-4 w-4" />Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )
    }
  ];

  const filterConfig = [
    { key: "customer_name", label: "Customer", type: "select" as const, options: [] },
    { key: "supplier_name", label: "Supplier", type: "select" as const, options: [] },
    { key: "status", label: "Status", type: "select" as const, options: [
      { value: "active", label: "Active" },
      { value: "inactive", label: "Inactive" },
      { value: "suspended", label: "Suspended" }
    ]}
  ];

  return (
    <ModuleAccessGuard moduleId="onboarding">
      <div className="space-y-6">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Accounts</h1>
            <p className="text-muted-foreground mt-2">Manage customer accounts and billing information.</p>
          </div>
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button variant="hero" onClick={resetForm}><Plus className="h-4 w-4 mr-2" />Create Account</Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingAccount ? "Edit Account" : "Create New Account"}</DialogTitle>
              </DialogHeader>
              <Tabs defaultValue="basic" className="w-full">
                <TabsList className="grid w-full grid-cols-5">
                  <TabsTrigger value="basic">Basic</TabsTrigger>
                  <TabsTrigger value="site">Site</TabsTrigger>
                  <TabsTrigger value="address">Address</TabsTrigger>
                  <TabsTrigger value="dates">Dates</TabsTrigger>
                  <TabsTrigger value="status">Status</TabsTrigger>
                </TabsList>
                <TabsContent value="basic" className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Customer *</Label>
                      <Input placeholder="Customer ID" value={formData.customer_id} onChange={(e) => setFormData({...formData, customer_id: e.target.value})} />
                    </div>
                    <div className="space-y-2">
                      <Label>Supplier *</Label>
                      <Input placeholder="Supplier ID" value={formData.supplier_id} onChange={(e) => setFormData({...formData, supplier_id: e.target.value})} />
                    </div>
                    <div className="space-y-2">
                      <Label>Account Number *</Label>
                      <Input value={formData.account_number} onChange={(e) => setFormData({...formData, account_number: e.target.value})} />
                    </div>
                    <div className="space-y-2">
                      <Label>Commodity</Label>
                      <Select value={formData.commodity} onValueChange={(val) => setFormData({...formData, commodity: val})}>
                        <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="electricity">Electricity</SelectItem>
                          <SelectItem value="gas">Gas</SelectItem>
                          <SelectItem value="water">Water</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Frequency</Label>
                      <Select value={formData.frequency} onValueChange={(val) => setFormData({...formData, frequency: val})}>
                        <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="monthly">Monthly</SelectItem>
                          <SelectItem value="quarterly">Quarterly</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Bill Source</Label>
                      <Input value={formData.bill_source} onChange={(e) => setFormData({...formData, bill_source: e.target.value})} />
                    </div>
                  </div>
                </TabsContent>
                <TabsContent value="site" className="space-y-4">
                  <div className="space-y-2">
                    <Label>Site</Label>
                    <Input placeholder="Site ID" value={formData.site_id} onChange={(e) => setFormData({...formData, site_id: e.target.value})} />
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox checked={hasParent} onCheckedChange={(checked) => setHasParent(!!checked)} />
                    <Label>Has Parent Account</Label>
                  </div>
                  {hasParent && (
                    <div className="space-y-2">
                      <Label>Parent Account Number</Label>
                      <Input value={formData.parent_account_number} onChange={(e) => setFormData({...formData, parent_account_number: e.target.value})} />
                    </div>
                  )}
                </TabsContent>
                <TabsContent value="address" className="space-y-4">
                  <div className="space-y-2">
                    <Label>Address</Label>
                    <Textarea value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>City</Label>
                      <Input value={formData.city} onChange={(e) => setFormData({...formData, city: e.target.value})} />
                    </div>
                    <div className="space-y-2">
                      <Label>State</Label>
                      <Input value={formData.state} onChange={(e) => setFormData({...formData, state: e.target.value})} />
                    </div>
                    <div className="space-y-2">
                      <Label>Country</Label>
                      <Input value={formData.country} onChange={(e) => setFormData({...formData, country: e.target.value})} />
                    </div>
                    <div className="space-y-2">
                      <Label>Postal Code</Label>
                      <Input value={formData.postal_code} onChange={(e) => setFormData({...formData, postal_code: e.target.value})} />
                    </div>
                  </div>
                </TabsContent>
                <TabsContent value="dates" className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Account Start Date *</Label>
                      <Input type="date" value={formData.account_start_date} onChange={(e) => setFormData({...formData, account_start_date: e.target.value})} />
                    </div>
                    <div className="space-y-2">
                      <Label>Account End Date</Label>
                      <Input type="date" value={formData.account_end_date} onChange={(e) => setFormData({...formData, account_end_date: e.target.value})} />
                    </div>
                    <div className="space-y-2">
                      <Label>Identifier Number</Label>
                      <Input value={formData.identifier_number} onChange={(e) => setFormData({...formData, identifier_number: e.target.value})} />
                    </div>
                  </div>
                </TabsContent>
                <TabsContent value="status" className="space-y-4">
                  <div className="space-y-2">
                    <Label>Status</Label>
                    <Select value={formData.status} onValueChange={(val) => setFormData({...formData, status: val})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                        <SelectItem value="closed">Closed</SelectItem>
                        <SelectItem value="suspended">Suspended</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </TabsContent>
              </Tabs>
              <div className="flex justify-end gap-2 mt-4">
                <Button variant="outline" onClick={() => setIsCreateOpen(false)}>Cancel</Button>
                <Button onClick={handleSubmit}>{editingAccount ? "Update" : "Create"}</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="shadow-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Accounts</p>
                  <p className="text-2xl font-bold">{accountsData?.total || 0}</p>
                </div>
                <CreditCard className="h-5 w-5 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
          <Card className="shadow-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Active</p>
                  <p className="text-2xl font-bold">{accountsData?.active || 0}</p>
                </div>
                <Users className="h-5 w-5 text-success" />
              </div>
            </CardContent>
          </Card>
          <Card className="shadow-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Inactive</p>
                  <p className="text-2xl font-bold">{accountsData?.inactive || 0}</p>
                </div>
                <Building className="h-5 w-5 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
          <Card className="shadow-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Suspended</p>
                  <p className="text-2xl font-bold">{accountsData?.suspended || 0}</p>
                </div>
                <Building className="h-5 w-5 text-warning" />
              </div>
            </CardContent>
          </Card>
        </div>

        <FilterBar
          searchPlaceholder="Search accounts..."
          searchValue={search}
          onSearchChange={setSearch}
          filters={filterConfig}
          activeFilters={filters}
          onFilterChange={(key, value) => setFilters({...filters, [key]: value})}
          onClearFilters={() => setFilters({})}
        />

        <DataTable
          data={accountsData?.data || []}
          columns={columns}
          totalCount={accountsData?.total || 0}
          pageSize={20}
          currentPage={page}
          onPageChange={setPage}
          isLoading={isLoading}
          emptyMessage="No accounts found"
          getRowId={(item) => item.id}
        />
      </div>
    </ModuleAccessGuard>
  );
}
